/*
	TEMPORARY — display-name overrides for the membership plan cards.

	The plan names in Resamania carry admin artefacts and are not customer-facing:

	    "2.0 Gym Off-Peak (Standard (+15)"     ← sort prefix, unclosed bracket
	    "5.0 - 1 Gym Session (Single rate)"    ← sort prefix, internal rate name

	The clean names shown on the existing Resamania-hosted checkout ("OFF - PEAK",
	"PAY AS YOU GO") are not reachable from our API scope:

	  - no field anywhere in the showcase_products response holds them
	  - GET /products returns 403 api.error.scope.unauthorized-endpoint for our
	    OAuth client, so product.name cannot be read

	So this map exists until one of the following lands, at which point DELETE IT:

	  1. The showcase product names are tidied in the Resamania admin (preferred —
	     fixes it at source for every consumer, including the existing checkout)
	  2. /products is added to our OAuth scope and turns out to carry the clean
	     names, in which case read them from there

	Keyed by showcase product IRI rather than by name or price. The name is the
	thing we are working around, and the price is the thing most likely to change —
	keying on either would silently drop the override exactly when it is needed.

	Anything not listed falls through to the name the API returns. That is
	deliberate: an unmapped product shows an ugly name rather than no name.
 */

export const PLAN_NAME_OVERRIDES: Record<string, string> = {
	// Club 156 — Feel Fit Gym. Matches the live Resamania checkout.
	'/feelfitgym/showcase_products/3162': 'Off - Peak',
	'/feelfitgym/showcase_products/3159': 'Peak',
	'/feelfitgym/showcase_products/1762': 'Holistic Peak',
	'/feelfitgym/showcase_products/1465': 'One Month',
	'/feelfitgym/showcase_products/1490': 'Pay As You Go',

	// Clubs 165 (Brazilian Jiu-Jitsu), 166 (Boxing) and 167 (Muay Thai) are not
	// mapped. Their catalogues need attention in Resamania first — see the note
	// in the pull request. Until then they fall through to cleanPlanName, which
	// strips the admin artefacts but cannot invent the checkout's own wording.
};

/*
	The three cleanup steps that turn a Resamania plan name into a display name,
	kept separate so each can be reasoned about and reused on its own. Run in
	order they take "1.0 Gym Peak (Standard (-15)" to "Gym Peak".
 */

/*
	Strips the sort prefix: "1.0 Gym Peak" -> "Gym Peak", "5.0 - 1 Gym Session" ->
	"1 Gym Session".

	The decimal point is required. Matching a bare leading integer would eat the
	"1" from a name like "1 Gym Session", which is part of the name rather than a
	prefix. The optional dash covers the "5.0 - " variant.
 */
export function stripVersionPrefix(name: string): string {
	return name.replace(/^\d+\.\d+\s*-?\s*/, '').trim();
}

/*
	Strips one closed parenthesised group from the end: "Gym Peak (Standard (-15)"
	-> "Gym Peak (Standard", "1 Gym Session (Single rate)" -> "1 Gym Session".

	These hold the rate variant, which is the same across plans and says nothing
	about the plan itself. Only a trailing group is touched, so a parenthesis in
	the middle of a name survives.
 */
export function stripTrailingParenthetical(name: string): string {
	return name.replace(/\s*\([^()]*\)\s*$/, '').trim();
}

/*
	Strips the dangling "(Standard" from the end: "Gym Peak (Standard" -> "Gym
	Peak".

	Resamania names carry an unclosed bracket — "(Standard (+15)" opens twice and
	closes once — so removing the closed group above leaves "(Standard" behind.

	Deliberately literal. A name ending in some other unclosed group keeps it,
	which is loud enough to notice and fix at source rather than being quietly
	tidied away here.
 */
export function stripStandardSuffix(name: string): string {
	return name.replace(/\s*\(Standard\s*$/, '').trim();
}

/*
	The three steps in the order they are meant to run.
 */
export function cleanPlanName(name: string): string {
	return stripStandardSuffix(stripTrailingParenthetical(stripVersionPrefix(name)));
}

/*
	The plan cards hold a whole showcase product; the checkout steps hold only the
	name and IRI snapshotted onto the session. Both resolve the display name here
	so the two screens cannot disagree.
 */
export function resolvePlanName(iri: string|null|undefined, name: string|null|undefined): string {
	const override = iri ? PLAN_NAME_OVERRIDES[iri] : null;

	if (override) {
		return override;
	}

	// An unmapped plan is cleaned rather than shown raw. This cannot reach the
	// names on the Resamania-hosted checkout — those are not in our API scope —
	// but it does strip the sort prefix and rate variant, which are admin
	// artefacts either way.
	return cleanPlanName(name ?? '');
}

export function getPlanName(showcaseProduct): string {
	return resolvePlanName(showcaseProduct?.['@id'], showcaseProduct?.name);
}
